/**
 * Cognito sign-in for the SPA (D5): **SRP** against the stage's SPA app
 * client — the password never leaves the browser — and REFRESH_TOKEN_AUTH to
 * renew.
 *
 * The protocol is `@acp/cognito-srp` (shared with the gfx designer, ACP-D45):
 * SRP through `amazon-cognito-identity-js` injected as `SrpLib` (its
 * `realSrpLib` wires the library over an in-memory storage), and the refresh
 * as one JSON POST over the injected fetch. This module maps its tokens to the
 * SPA's `Session`; persistence is `session.ts`'s.
 */
import type { FetchPort } from '@acp/api-client/ports.js';
import { refreshCognitoTokens } from '@acp/cognito-srp/refresh.js';
import { type CognitoClientConfig, type SrpLib, type SrpOutcome, srpSignIn } from '@acp/cognito-srp/srp.js';
import { type Session, sessionFromTokens } from './session.js';

/** How the shared sign-in names this app when it refuses a challenge (MFA, …) it does not support. */
const APP_NAME = 'the ACP web app';

export type LoginOutcome =
  | { readonly type: 'signed-in'; readonly session: Session }
  | { readonly type: 'new-password-required'; readonly complete: (newPassword: string) => Promise<LoginOutcome> };

function toLoginOutcome(outcome: SrpOutcome): LoginOutcome {
  if (outcome.type === 'new-password-required') {
    // An admin-created user's first sign-in: the pool asks for a new password.
    return { type: 'new-password-required', complete: async (newPassword) => toLoginOutcome(await outcome.complete(newPassword)) };
  }
  const { idToken, accessToken, refreshToken } = outcome.tokens;
  const session = sessionFromTokens('cognito', idToken, accessToken, refreshToken);
  if (session === null) throw new Error('Cognito answered an ID token without the claims the SPA needs');
  return { type: 'signed-in', session };
}

/** Sign in with SRP. Rejects with Cognito's message (wrong password, unknown user, …). */
export async function cognitoLogin(lib: SrpLib, email: string, password: string): Promise<LoginOutcome> {
  return toLoginOutcome(await srpSignIn(lib, email, password, APP_NAME));
}

/** Renew with the refresh token: the renewed session, null when Cognito refuses it, or a rejection when the attempt failed transiently. */
export async function refreshCognitoSession(fetch: FetchPort, config: CognitoClientConfig, session: Session): Promise<Session | null> {
  if (session.refreshToken === null) return null;
  const renewed = await refreshCognitoTokens(fetch, config, session.refreshToken);
  if (renewed === null) return null;
  // Cognito keeps the original refresh token valid and does not send a new one.
  return sessionFromTokens('cognito', renewed.idToken, renewed.accessToken, session.refreshToken);
}
